import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import {
    IAggregation,
    IColorBandObject,
    ITableColumns,
} from 'app/modules/reports/interfaces/reporting-data';
import { formatDate } from '@angular/common';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportingService } from 'app/modules/reports/services/reporting.service';

@Component({
    selector: 'custom-mat-table',
    templateUrl: './custom-mat-table.component.html',
    styleUrls: ['./custom-mat-table.component.scss'],
})
export class CustomMatTableComponent implements OnInit, OnChanges, OnDestroy {
    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _reportingService: ReportingService,
        private _reusableFunctions: ReusableFunctions
    ) {}

    @Input() customTableInfo: ICustomTableInfoExtend;
    @Input() footerObject?: IFooterObject;
    @Output() contentChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() sortChanged: EventEmitter<Array<Sort>> = new EventEmitter<Array<Sort>>();
    @Output() pageChanged: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
    @Output() cellClickedEvent: EventEmitter<ICellClickEventInfo> =
        new EventEmitter<ICellClickEventInfo>();
    @Output() inputChangeEvent: EventEmitter<ICellClickEventInfo> =
        new EventEmitter<ICellClickEventInfo>();

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    tableId: string;
    noDataType: NoDataTypes;
    displayedColumns: string[];
    headersInfo: ITableColumns[];
    bandColorList: IColorBandObject[];
    footerSummaryList: IFooterSummary[];
    hasTableData: boolean;
    isDataReady: boolean;
    pageEvent: PageEvent;
    pageSizeOptions = [10, 20, 50, 100, 150];
    pageSize = 10;
    paginationLength: number;
    showFirstLastButtons = true;
    paginationDisabled = false;
    showPaginator: boolean = false;
    isTheFirstLoading: boolean = false;
    dataSource = new MatTableDataSource<Task>([]);
    oldSearchTerm: string;
    customTableInfoChange: SimpleChange;
    searchTimeout: number;
    isSearching = false;

    ngOnInit() {
        this.displayedColumns = [];
        this.footerSummaryList = [];
        this.hasTableData = false;
        this.isDataReady = false;
        this.isTheFirstLoading = true;
        this.isSearching = false;
        this.searchTimeout = 1000;
        this.oldSearchTerm = '';
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: '',
            inputSource: NoDataSourceType.largeDataCard,
            wrapInACard: false,
        };
    }

    ngOnChanges(changes: { [property: string]: SimpleChange }) {
        // TODO: add logic to capture only the changed items and limit unnecessary reloads.
        this.isTheFirstLoading = true;
        this.customTableInfoChange = changes['customTableInfo'];
        if (this.customTableInfoChange) {
            let cardData: ICustomTableInfoExtend = this.customTableInfoChange.currentValue;
            this.showPaginator = cardData?.showPaginator ?? true;
            if (cardData.columnInfoList && cardData.columnInfoList?.length > 0) {
                this.setInitialStatusInfo(cardData.columnInfoList);
                this.headersInfo = cardData.columnInfoList;
                this.updateData(cardData);

                if (cardData.dataHandleInUiSide) {
                    this.setAggregationUiSide(cardData);
                }

                this.setInitialSorting(cardData.columnInfoList);
                if (this.hasRowsInDisplay()) {
                    this.emitContentChange();
                    this.isTheFirstLoading = false;
                }
            }
        }

        let summaryChange: SimpleChange = changes['footerObject'];
        if (summaryChange) {
            let footerObject: IFooterObject = summaryChange.currentValue;
            this.setAggregationServerSide(footerObject?.footerSummary);
        }
    }

    setInitialStatusInfo(headersInfo: ITableColumns[]) {
        // TODO: add logic to run this only once
        this.displayedColumns = [];
        headersInfo.sort((a, b) => a.fieldOrder - b.fieldOrder);
        headersInfo.forEach((header, index) => {
            if (
                (header.cellClickEventType === undefined || header.cellClickEventType === null) &&
                header.isHyperlink === true
            ) {
                header.cellClickEventType = 'hyperlink';
            }

            if (!(header.hidden || header.hideIf === 'ui')) {
                this.displayedColumns.push(header.fieldId);
            }
            header.fieldOrder = index;
        });
    }

    setInitialSorting(headersInfo: ITableColumns[]) {
        if (this.hasRowsInDisplay()) {
            let firstSortableHeader = headersInfo.find(
                (header) => header.isSortable && header.defaultSorting
            );

            if (firstSortableHeader) {
                if (this.customTableInfo.dataHandleInUiSide) {
                    // TODO: find a solution to do sorting on multiple columns
                    this.sort.sort({ id: '', start: '', disableClear: true });
                    this.sort.sort({
                        id: firstSortableHeader.fieldId,
                        start: firstSortableHeader.defaultSorting as any,
                        disableClear: true,
                    });
                }
            }
        }

        if (this.customTableInfo.dataHandleInUiSide) {
            this.dataSource.sortingDataAccessor = (data: any, property: string) => {
                if (data && data[property]) {
                    let column = headersInfo.find((header) => header.fieldId === property);

                    if (column) {
                        switch (column.dataType) {
                            case 'string':
                                return data[property]
                                    ? data[property].toString().toLowerCase()
                                    : '';
                            default:
                                return data[property];
                        }
                    }
                    return data[property];
                }
                return '';
            };
        }
    }

    updateData(cardData: ICustomTableInfoExtend) {
        this.dataSource.data = [];
        this.hasTableData = cardData?.hasData ?? false;
        this.isDataReady = cardData?.isDataReady ?? false;
        if (this.noDataType) {
            this.noDataType.action = cardData?.action ?? NoDataActionType.pageStart;
            this.noDataType.searchTerm = cardData.searchTerm;
            this.noDataType.componentTitle = cardData.title;
        }

        if (cardData) {
            this.tableId = this.customTableInfo.tableId;
            this.dataSource.data = cardData.dataSet?.length > 0 ? cardData.dataSet : [];
            this.bandColorList = cardData.bandColorList;
            this.setSearchTerm();
            this.setPaginator();
        } else {
            this.hasTableData = false;
            this.isDataReady = true;
            this.dataSource.data = [];
        }
    }

    setPaginator() {
        let paginator: MatPaginator = null;
        if (this.customTableInfo.dataHandleInUiSide) {
            paginator = this.paginator;
        }

        if (this.customTableInfo.paginationOptions) {
            this.paginationLength = this.customTableInfo.paginationOptions.totalRecords ?? 0;
            this.pageSizeOptions =
                this.customTableInfo.paginationOptions.pageSizeOptions ?? this.pageSizeOptions;
            this.pageSize = this.customTableInfo.paginationOptions.pageSize ?? this.pageSize;
        }

        this.dataSource.paginator = paginator;
    }

    setSearchTerm() {
        let currentSearchTerm = this.customTableInfo?.searchTerm ?? '';
        if (this.customTableInfo.dataHandleInUiSide) {
            this.isSearching = true;
            if (currentSearchTerm !== this.oldSearchTerm) {
                setTimeout(() => {
                    this.dataSource.filter = currentSearchTerm;
                    this.isSearching = false;
                    this.oldSearchTerm = currentSearchTerm;
                    this.noDataType.action = NoDataActionType.search;
                }, this.searchTimeout);
            } else {
                this.oldSearchTerm = currentSearchTerm;
                this.isSearching = false;
            }
        }
    }

    sortChange(event: Sort) {
        let colInf = this.headersInfo.find((h) => h.fieldId === event.active);
        if (colInf && colInf.isSortable) {
            if (this.customTableInfo.dataHandleInUiSide) {
                this.dataSource.sort = this.sort;
            }
            this.sortChanged.emit([event]);
        }
    }

    dropdownChanged(selectedItem, cellItem: object, field: ITableColumns) {
        this.inputChangeEvent.emit({
            columnInfo: field,
            cellData: cellItem,
            cellValue: selectedItem,
        });
    }

    inputChangeHandler(value: object, cellItem: object, field: ITableColumns) {
        this.inputChangeEvent.emit({
            columnInfo: field,
            cellData: cellItem,
            cellValue: value,
        });
    }

    handlePageEvent(event: PageEvent) {
        this.pageEvent = event;
        this.pageChanged.emit(this.pageEvent);
    }

    setContainerStyle() {
        // TODO: add logic only to call once per page load
        let style = {};
        let height = 0;
        if (this.customTableInfo?.tableHeight) {
            height = this.customTableInfo?.tableHeight;
        } else {
            height = 500;
        }
        style['height'] = height + 'px';
        return style;
    }

    formatData(cellItem: object, field: ITableColumns) {
        let value = '';
        let originalValue = cellItem[field.fieldId]?.toString();
        if (originalValue && originalValue !== '') {
            switch (field.dataType) {
                case 'currency':
                    value = this._reusableFunctions.formatNumber(
                        originalValue,
                        field.decimalPlaces ?? 0,
                        'currency',
                        true
                    );
                    break;
                case 'money':
                    value = this._reusableFunctions.formatNumber(
                        originalValue,
                        field.decimalPlaces ?? 0,
                        'currency',
                        true
                    );
                    break;
                case 'date':
                    value = formatDate(originalValue, 'MM/dd/yyyy', 'en-US', 'UTC');
                    break;
                case 'dateTime':
                    value = formatDate(originalValue, 'MM/dd/yyyy, hh:mm a', 'en-US', 'UTC');
                    break;
                case 'percent':
                    value = this._reusableFunctions.formatNumber(
                        originalValue,
                        field.decimalPlaces ?? 0,
                        'percentage',
                        true
                    );
                    break;
                case 'number':
                    value = this._reusableFunctions.formatNumber(
                        originalValue,
                        field.decimalPlaces ?? 0,
                        'number',
                        true
                    );
                    break;
                case 'boolean':
                    value = this.capitalizeFirstLetter(originalValue);
                    break;
                default:
                    value = originalValue;
            }
        }

        return value;
    }

    setAggregationServerSide(footerSummary: IAggregation[]) {
        this.footerSummaryList = [];
        let columnInfoList = this.customTableInfo?.columnInfoList;
        if (columnInfoList?.length > 0 && footerSummary?.length > 0) {
            let aggValue = '';

            columnInfoList.forEach((field, index) => {
                if (field.fieldOrder === 0) {
                    aggValue = 'Summary';
                } else if (field.aggregationType !== undefined) {
                    let footer = footerSummary.find((f) => f.fieldId === field.fieldId);
                    aggValue = '';
                    if (footer?.value) {
                        let aggregationType =
                            field.aggregationType &&
                            (field.aggregationType === 'countNoNull' ||
                                field.aggregationType === 'count')
                                ? 'number'
                                : field.dataType;

                        let value = this._reusableFunctions.formatNumber(
                            footer.value,
                            field.decimalPlaces ?? 0,
                            aggregationType,
                            true
                        );
                        let prefix = this.setSummaryPrefix(field.aggregationType);

                        aggValue = `${prefix}: ${value}`;
                    }
                } else {
                    aggValue = '';
                }
                this.footerSummaryList.push({
                    fieldId: field.fieldId,
                    aggValue: aggValue,
                });
            });
        }
    }

    setSummaryPrefix(aggregationType: string) {
        let prefix = '';
        switch (aggregationType) {
            case 'sum':
                prefix = 'Total';
                break;
            case 'count':
                prefix = 'Count';
                break;
            case 'countNoNull':
                prefix = 'Count';
                break;
            case 'average':
                prefix = 'Average';
                break;
            case 'averageNoNull':
                prefix = 'Average';
                break;
            case 'min':
                prefix = 'Min';
                break;
            case 'max':
                prefix = 'Max';
                break;
            default:
                prefix =
                    aggregationType?.length > 0
                        ? this._reusableFunctions.toCamelCase(aggregationType)
                        : '';
                break;
        }
        return prefix;
    }

    setAggregationUiSide(cardData: ICustomTableInfoExtend) {
        // TODO: Add logic to call only once after data loaded.
        this.footerSummaryList = [];
        let columnInfoList = cardData?.columnInfoList;
        if (this.customTableInfo.dataHandleInUiSide) {
            if (columnInfoList?.length > 0) {
                let aggValue = '';

                columnInfoList.forEach((field, index) => {
                    if (field.fieldOrder === 0) {
                        aggValue = 'Summary';
                    } else if (field.aggregationType !== undefined) {
                        aggValue = this.calculateAggregation(field);
                    } else {
                        aggValue = '';
                    }
                    this.footerSummaryList.push({
                        fieldId: field.fieldId,
                        aggValue: aggValue,
                    });
                });
            }
        }
    }

    calculateAggregation(field: ITableColumns) {
        let aggValue = '';
        //TODO: Add logic to do custom-aggregation.

        if (this.customTableInfo.dataHandleInUiSide) {
            let data = this.customTableInfo.dataSet;
            if (data?.length > 0) {
                try {
                    let value = '';
                    switch (field.aggregationType) {
                        case 'count':
                            value = this._reusableFunctions.formatNumber(
                                data.length,
                                field.decimalPlaces ?? 0,
                                'number',
                                true
                            );
                            break;
                        case 'countNoNull':
                            let notNullData = data.filter((d) => d[field.fieldId] !== null);
                            value = this._reusableFunctions.formatNumber(
                                notNullData.length,
                                field.decimalPlaces ?? 0,
                                'number',
                                true
                            );
                            break;
                        case 'sum':
                            let sum = this._reusableFunctions.calculateSumByProperty(
                                data,
                                field.fieldId
                            );
                            value = this._reusableFunctions.formatNumber(
                                sum,
                                field.decimalPlaces ?? 0,
                                field.dataType,
                                true
                            );
                            break;
                        case 'average':
                            // Note: This does just the basic calc: sum/total-record-count.
                            // Use custom-aggregation for complex situations.
                            let tot = this._reusableFunctions.calculateSumByProperty(
                                data,
                                field.fieldId
                            );
                            value = this._reusableFunctions.formatNumber(
                                tot / data?.length,
                                field.decimalPlaces ?? 0,
                                field.dataType,
                                true
                            );
                            break;
                        case 'averageNoNull':
                            // Note: This does just the basic calc: sum/total-not-null-record-count.
                            // Use custom-aggregation for complex situations.
                            let notNullDataForAvg = data.filter((d) => d[field.fieldId] !== null);
                            if (notNullDataForAvg?.length > 0) {
                                let totNoNull = this._reusableFunctions.calculateSumByProperty(
                                    notNullDataForAvg,
                                    field.fieldId
                                );

                                value = this._reusableFunctions.formatNumber(
                                    totNoNull / notNullDataForAvg?.length,
                                    field.decimalPlaces ?? 0,
                                    field.dataType,
                                    true
                                );
                            } else {
                                value = '';
                            }
                            break;
                        default:
                            value = '';
                            break;
                    }

                    let prefix = this.setSummaryPrefix(field.aggregationType);
                    aggValue = `${prefix}: ${value}`;
                } catch (err) {}
            }
        } else {
        }

        return aggValue;
    }

    getAggregation(field: ITableColumns) {
        let aggValue = '';
        if (this.footerSummaryList?.length > 0) {
            let aggInf = this.footerSummaryList.find((agg) => agg.fieldId === field.fieldId);
            if (aggInf) {
                aggValue = aggInf.aggValue;
            }
        }
        return aggValue;
    }

    capitalizeFirstLetter(word: string): string {
        let temp: string = String(word);
        return temp.charAt(0).toUpperCase() + temp.slice(1);
    }

    setCellStyle(dataItem: object, fieldInfo: ITableColumns) {
        let style = this.getCommonCellStyles(dataItem, fieldInfo);

        let leftPadding = '5px';
        let rightPadding = '5px';

        if (fieldInfo.fieldId === this.displayedColumns[0]) {
            leftPadding = '12px';
        }

        if (fieldInfo.dataAlignment === 'right') {
            rightPadding = '10px';
        }

        style['padding'] = '0 ' + rightPadding + ' 5px ' + leftPadding;
        return style;
    }

    setHeaderStyle(fieldInfo: ITableColumns) {
        /* TODO: Seems like this methods is running every time the mouse move over the table.
            Update the logic to run only it is required. */
        let leftPadding = '5px';
        let rightPadding = '5px';

        if (fieldInfo.fieldId === this.displayedColumns[0]) {
            leftPadding = '14px';
        }

        if (fieldInfo['text-align'] === 'right') {
            rightPadding = '10px';
        }

        let styles = {
            'text-align': fieldInfo.headerAlignment,
            padding: '5px ' + rightPadding + ' 5px ' + leftPadding,
            'line-height': '40px',
            color: '#333333',
            'font-size': '13px',
            'text-transform': 'uppercase',
            width: fieldInfo.width ? fieldInfo.width + 'px' : '',
            'background-color': fieldInfo?.headerBackgroundColor ?? '',
        };
        this.setHeaderTextAlignment(fieldInfo);
        return styles;
    }

    setFooterStyle(fieldInfo: ITableColumns) {
        let leftPadding = '5px';
        let rightPadding = '5px';

        if (fieldInfo.fieldId === this.displayedColumns[0]) {
            leftPadding = '14px';
        }

        if (fieldInfo['text-align'] === 'right') {
            rightPadding = '10px';
        }

        let styles = {
            'text-align': fieldInfo.headerAlignment,
            padding: '5px ' + rightPadding + ' 5px ' + leftPadding,
            'line-height': '40px',
            'font-size': '13px',
        };

        return styles;
    }

    setHeaderTextAlignment(fieldInfo: ITableColumns) {
        let headerCell = document.getElementById('custom-mat-header-th-' + fieldInfo.fieldId);
        if (headerCell) {
            let headerContainer = headerCell.getElementsByClassName('mat-sort-header-container');
            headerContainer
                .item(0)
                ?.setAttribute('style', 'justify-content:' + fieldInfo.headerAlignment);
        }
    }

    getCommonCellStyles(dataItem: object, fieldInfo: ITableColumns) {
        let styles = {
            color: '',
            'background-color': '',
            'text-align': fieldInfo?.dataAlignment ?? '',
            'min-width': fieldInfo.width + 'px',
        };

        if (dataItem) {
            let value = dataItem[fieldInfo.fieldId];
            if (fieldInfo.textDefaultColor && typeof fieldInfo.textDefaultColor === 'string') {
                styles['color'] = fieldInfo.textDefaultColor;
            }

            if (fieldInfo.textColorThresholdFileldId) {
                let thresoldValue = dataItem[fieldInfo.textColorThresholdFileldId];
                if (typeof thresoldValue === 'number' && typeof value === 'number') {
                    if (thresoldValue > value) {
                        styles['color'] = fieldInfo.textPositiveColor;
                    } else if (thresoldValue < value) {
                        styles['color'] = fieldInfo.textNegativeColor;
                    } else {
                        styles['color'] = fieldInfo.textDefaultColor;
                    }
                }
            }

            if (
                fieldInfo.backgroundColorThresholdFileldId &&
                value !== null &&
                value !== undefined
            ) {
                styles['line-height'] = '46px';
                styles['min-height'] = '46px';

                if (
                    fieldInfo.backgroundDefaultColor &&
                    typeof fieldInfo.backgroundDefaultColor === 'string'
                ) {
                    styles['background-color'] = fieldInfo.backgroundDefaultColor;
                }

                if (fieldInfo.backgroundColorThresholdFileldId) {
                    let thresoldValueBackground =
                        dataItem[fieldInfo.backgroundColorThresholdFileldId];
                    if (typeof thresoldValueBackground === 'number' && typeof value === 'number') {
                        if (thresoldValueBackground > value) {
                            styles['background-color'] = fieldInfo.backgroundPositiveColor;
                        } else if (thresoldValueBackground < value) {
                            styles['background-color'] = fieldInfo.backgroundNegativeColor;
                        } else {
                            styles['background-color'] = fieldInfo.backgroundDefaultColor;
                        }
                    }
                }
            }
            if (fieldInfo.setBackgroundColorByBand && value !== null && value !== undefined) {
                styles['line-height'] = '46px';
                styles['min-height'] = '46px';

                if (fieldInfo.backgroundColorSetFieldId) {
                    let bandId = dataItem[fieldInfo.backgroundColorSetFieldId];
                    let colorInfo = this.bandColorList.find((band) => band.colorbandId === bandId);
                    styles['background-color'] = colorInfo ? colorInfo.color : '';
                }
            }

            if (fieldInfo.cellClickEventType === 'hyperlink' || fieldInfo.isHyperlink) {
                styles['color'] = '#0078D7';
                styles['cursor'] = 'pointer';
            }
        }
        return styles;
    }

    matContentChanged() {
        if (!this.isTheFirstLoading) {
            this.emitContentChange();
        }
    }

    emitContentChange() {
        let data = this.dataSource?.connect()?.value;

        if (this.hasRowsInDisplay()) {
            this.contentChanged.emit(data);
        } else {
            this.hasTableData = false;
        }
    }

    hasRowsInDisplay() {
        let currentRowsInDisplay = this.dataSource?.connect()?.value;
        return currentRowsInDisplay && currentRowsInDisplay.length > 0;
    }

    cellClickHandler(dataItem: object, fieldInfo: ITableColumns) {
        // TODO: maybe more restrictions are needed for other types such as checkbox etc
        if (fieldInfo.dataType === 'icon' || fieldInfo.cellClickEventType === 'iconClick') {
            return;
        } else if (fieldInfo.cellClickEventType) {
            this.cellClickedEvent.emit({ columnInfo: fieldInfo, cellData: dataItem });
        }
    }

    iconClickHandler(dataItem: any, fieldInfo: ITableColumns) {
        if (!(fieldInfo.dataType === 'icon' || fieldInfo.cellClickEventType === 'iconClick')) {
            return;
        }
        this.cellClickedEvent.emit({ columnInfo: fieldInfo, cellData: dataItem });
    }

    getIconButtonDisableStatus(dataItem: any, fieldInfo: ITableColumns) {
        if (fieldInfo.iconProperties?.iconDisableKey) {
            return dataItem[fieldInfo.iconProperties.iconDisableKey] ?? true;
        }
        return false;
    }

    getIconStyle(dataItem: any, fieldInfo: ITableColumns) {
        let iconStyles = fieldInfo.iconProperties?.iconStyle ?? {};
        if (!iconStyles['font-size']) {
            iconStyles['font-size'] = '15px';
        }
        return iconStyles;
    }

    getIcon(dataItem: any, fieldInfo: ITableColumns) {
        if (
            fieldInfo.iconProperties?.setIconByCellValue &&
            fieldInfo.iconProperties?.iconToChangeByKey &&
            fieldInfo.iconProperties?.iconToChangeByValue
        ) {
            let styleClass = fieldInfo.iconProperties?.iconClass;
            let value = dataItem[fieldInfo.iconProperties.iconToChangeByKey];
            if (value) {
                styleClass =
                    value.toString().toLowerCase() ===
                    fieldInfo.iconProperties.iconToChangeByValue.toString()
                        ? fieldInfo.iconProperties.iconClassChange
                        : fieldInfo.iconProperties.iconClassStart;
            }

            return styleClass;
        } else {
            return fieldInfo.iconProperties?.iconClass ?? '';
        }
    }

    headerIconClick(fieldInfo: ITableColumns) {}

    getDdlStyle(dataItem: any, fieldInfo: ITableColumns) {
        let styles = { width: '90%', 'min-width': '50px' };

        if (
            fieldInfo?.dataInputProperties?.widthPercent &&
            fieldInfo?.dataInputProperties?.widthPercent < 90
        ) {
            styles['width'] = fieldInfo?.dataInputProperties?.widthPercent + '%';
        }

        return styles;
    }

    isConventionalDataType(fieldInfo: ITableColumns) {
        switch (fieldInfo.dataType) {
            case 'money':
                return true;
            case 'string':
                return true;
            case 'number':
                return true;
            case 'percent':
                return true;
            case 'date':
                return true;
            case 'dateTime':
                return true;
            case 'currency':
                return true;
            case 'boolean':
                return true;
            default:
                return false;
        }
    }

    ngOnDestroy() {
        this.dataSource.disconnect();
    }
}

export interface ICustomTableInfoExtend {
    tableId: string;
    columnInfoList: ITableColumns[];
    tableHeight: number;
    hasData: boolean;
    isDataReady: boolean;
    dataSet: any;
    action: NoDataActionType;
    searchTerm: string;
    title: string;
    bandColorList: IColorBandObject[];
    dataHandleInUiSide: boolean;
    showSummary: boolean;
    showPaginator?: boolean;
    paginationOptions?: IPaginationOptions;
}

export interface IFooterObject {
    footerSummary: IAggregation[];
}

export interface ICellClickEventInfo {
    columnInfo: ITableColumns;
    cellData: object;
    cellValue?: any;
}

export interface IPaginationOptions {
    totalRecords: number;
    pageSize: number;
    pageSizeOptions: Array<number>;
}

export interface IFooterSummary {
    fieldId: string;
    aggValue: string;
}
